import React, { Component } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from '@unite-us/ui';
import { removeSelectedContact } from 'actions/Contact';
import Header from 'common/display/ContactStepper/components/Header';
import FormForm from 'common/form/FormForm';
import { browserHistory } from 'common/utils/browserHistory';
import { scrn7ScreeningsCompletion } from 'common/utils/FeatureFlags/flags';
import enforcedConsentRoute from 'common/utils/Navigation/enforcedConsentRoute';
import addNotification from 'common/utils/Notifications/actions/AddNotification';
import { coreApi } from 'src/api/config';
import { DOMAIN_CONSTANTS } from 'src/common/constants';
import { buildAccessibilityMessageFromErrors } from 'common/utils/Form';

export class AddSupportingContactDetailsStep extends Component {
  constructor(props) {
    super(props);
    this.onNextClick = this.onNextClick.bind(this);
    this.saveAndContinue = this.saveAndContinue.bind(this);
    this.onSaveCaseAssessments = this.onSaveCaseAssessments.bind(this);
    this.state = {
      expandedFormCard: 0,
      formsToLoad: _.map(props.profileForms, 'form.id'),
      showErrors: false,
      isSubmitting: false,
      errorCount: 0,
      accessibilityMessage: '',
      accessibilityMessageKey: 0,
    };

    this.formRefs = {};
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (JSON.stringify(this.state) !== JSON.stringify(nextState)) {
      return true;
    }
    return false;
  }

  handleExpandChange = (index) => {
    this.setState({ expandedFormCard: index });
  }

  async handleNextClick() {
    try {
      this.setState({ isSubmitting: true });
      await this.saveAndContinue();
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  onNextClick = async () => {
    this.setState({
      accessibilityMessage: '',
      accessibilityMessageKey: this.state.accessibilityMessageKey + 1,
    });
    return this.handleNextClick();
  }

  onFormLoaded = (id) => {
    this.setState((prevState) => {
      const newFormsToLoad = _.without(prevState.formsToLoad, id);
      return { formsToLoad: newFormsToLoad };
    });
  }

  async onSaveCaseAssessments() {
    try {
      const assessmentsResult = await this.submitCaseAssessments();
      if (!assessmentsResult) {
        this.props.addNotification({
          payload: {
            status: 'warning',
            statusText: 'Please complete the required fields in order to continue.',
          },
        });
        return false;
      }

      await Promise.all(assessmentsResult);

      this.props.addNotification({
        payload: {
          status: 'success',
          statusText: 'Supporting Assessments Successfully Saved',
        },
      });
      return true;
    } catch (e) {
      this.props.addNotification({
        payload: {
          status: 'warning',
          statusText: 'There was a problem saving the assessment with your contact.',
        },
      });
      return false;
    }
  }

  getErrorCount = () => Object.keys(this.formRefs).filter((k) => this.formRefs[k]?.ref?.hasErrors()).length;

  updateAccessibilityMessage = (clearFirst = false, initialDelay = 200, buildDelay = 50) => {
    buildAccessibilityMessageFromErrors({
      getErrorCount: () => this.getErrorCount(),
      setState: (updates, callback) => this.setState(updates, callback),
      clearFirst,
      initialDelay,
      buildDelay,
    });
  }

  submitCaseAssessments = () => {
    this.setState({ showErrors: true });

    const hasErrors = Object.keys(this.formRefs).some((k) => this.formRefs[k]?.ref?.hasErrors());
    if (hasErrors) {
      this.updateAccessibilityMessage(false, 200, 300);
      return null;
    }

    const data = Object.keys(this.formRefs).map((k) => {
      const requestBody = this.formRefs[k].buildRequestBody();
      return coreApi.createRecord('form_submission', requestBody);
    });

    return data;
  }

  goToConsentStep = () => {
    const { contact } = this.props;
    enforcedConsentRoute({
      contact,
      itemType: DOMAIN_CONSTANTS.CONTACT,
      to: `/facesheet/${contact.id}`,
    });
    this.props.removeSelectedContact();
  }

  async saveAndContinue() {
    const {
      contact,
      contactId,
      activeStep,
      clientRoute,
      scrn7ScreeningsCompletionEnabled,
      context,
    } = this.props;

    if (!await this.onSaveCaseAssessments()) return;

    if (!clientRoute && activeStep.onStepComplete) {
      if (!context.includes(DOMAIN_CONSTANTS.SCREENING)) {
        browserHistory.push({
          pathname: '/referrals/create/add-resources',
          search: `?person=${contactId}`,
          state: { source: 'client' },
        });
      } else if (
        scrn7ScreeningsCompletionEnabled &&
        context.includes(DOMAIN_CONSTANTS.SCREENING)
      ) {
        const screeningPath = `/facesheet/${contactId}/screenings`;
        enforcedConsentRoute({
          contact,
          itemType: DOMAIN_CONSTANTS.SCREENING,
          to: screeningPath,
        });
      } else {
        activeStep.onStepComplete({
          contact,
          contactId,
          eventTracker: this.context.eventTracker,
          removeSelectedContact: this.props.removeSelectedContact,
        });
      }
    } else {
      this.goToConsentStep();
    }
  }

  renderErrorSummary(accessibilityMessage) {
    return (
      <div
        key={this.state.accessibilityMessageKey}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {accessibilityMessage}
      </div>
    );
  }

  render() {
    const {
      profileForms,
      groupId,
      contactId,
      currentEmployee,
      header,
    } = this.props;

    const {
      showErrors,
      isSubmitting,
      formsToLoad,
      accessibilityMessage,
      errorCount,
    } = this.state;
    const nextDisabled = isSubmitting || formsToLoad.length > 0;
    const showErrorSummary = showErrors && errorCount > 0;

    return (
      <div>
        <div className="referral-header">
          <Header
            header={header}
            subHeader="Add Supporting Information"
          />
        </div>

        {showErrorSummary && this.renderErrorSummary(accessibilityMessage)}

        {
          profileForms.map((formUsage, index) => (
            <FormForm
              id={`group-form-${index}`}
              className={`custom-form-${index}`}
              setRef={(c) => { this.formRefs[formUsage.form.id] = c; }}
              key={`${formUsage.id}formform`}
              formId={formUsage.form.id}
              groupId={groupId}
              handleExpandChange={() => this.handleExpandChange(index)}
              expanded={index === this.state.expandedFormCard}
              onFormLoaded={this.onFormLoaded}
              currentEmployee={currentEmployee}
              hasErrors={showErrors ? this.formRefs[formUsage.form.id]?.ref?.hasErrors() : false}
              contextType="person"
              context={contactId}
              usage_type="profile"
            />
          ))
        }

        <form>
          <footer className="referral-footer">
            <div style={{ float: 'right' }}>
              <Button
                id="next-btn"
                className="ml-one"
                onClick={this.onNextClick}
                label="Next"
                primary
                disabled={nextDisabled}
              />
            </div>
          </footer>
        </form>
      </div>
    );
  }
}

AddSupportingContactDetailsStep.propTypes = {
  addNotification: PropTypes.func.isRequired,
  contact: PropTypes.object.isRequired,
  profileForms: PropTypes.array,
  contactId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
  removeSelectedContact: PropTypes.func.isRequired,
  header: PropTypes.string.isRequired,
  activeStep: PropTypes.object.isRequired,
  clientRoute: PropTypes.bool.isRequired,
  currentEmployee: PropTypes.object.isRequired,
  scrn7ScreeningsCompletionEnabled: PropTypes.bool,
  context: PropTypes.string,
};

AddSupportingContactDetailsStep.defaultProps = {
  profileForms: [],
  scrn7ScreeningsCompletionEnabled: false,
  context: '',
};

function mapStateToProps(state, ownProps) {
  const {
    selectedContact: contactId,
  } = state;

  const groupId = _.get(state, 'session.groupId');
  const contacts = _.get(state, 'contacts.contacts', []);
  const contact = _.find([...state.searchedContacts, ...contacts], { id: contactId }) || {};
  const currentEmployee = _.get(state, 'globalState.currentEmployee', {});

  return {
    contact,
    contactId,
    currentEmployee,
    groupId,
    profileForms: ownProps.profileForms,
    scrn7ScreeningsCompletionEnabled: scrn7ScreeningsCompletion(state),
  };
}

export default connect(mapStateToProps, {
  addNotification,
  removeSelectedContact,
})(AddSupportingContactDetailsStep);
